import React, { useState, useEffect } from 'react';
import { Shield, KeyRound, CheckCircle, XCircle, RefreshCw, AlertTriangle, FlaskConical } from 'lucide-react';

const SecuritySettings = ({ user }) => {
  const [twoFAEnabled, setTwoFAEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [setupMode, setSetupMode] = useState(false);
  const [disableMode, setDisableMode] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const isSuperAdmin = user?.role === 'super_admin';
  const [stripeTestMode, setStripeTestMode] = useState(false);
  const [stripeToggleLoading, setStripeToggleLoading] = useState(false);
  const [stripeStatusLoading, setStripeStatusLoading] = useState(isSuperAdmin);

  useEffect(() => {
    check2FAStatus();
    if (isSuperAdmin) checkStripeTestMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkStripeTestMode = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin/stripe-test-mode', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStripeTestMode(!!data.stripe_test_mode);
    } catch (error) {
      console.error('Failed to check Stripe test mode:', error);
    } finally {
      setStripeStatusLoading(false);
    }
  };

  const handleToggleStripeTestMode = async () => {
    const next = !stripeTestMode;
    setStripeToggleLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/admin/stripe-test-mode', {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: next })
      });
      const data = await response.json();
      if (data.success) {
        setStripeTestMode(data.stripe_test_mode);
      } else {
        setMessage(data.error || 'Failed to update Stripe test mode');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to update Stripe test mode');
      setMessageType('error');
    } finally {
      setStripeToggleLoading(false);
    }
  };

  const check2FAStatus = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/2fa/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setTwoFAEnabled(data.enabled);
      }
    } catch (error) {
      console.error('Failed to check 2FA status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async () => {
    setActionLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/2fa/setup', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setQrCode(data.qrCode);
        setSecret(data.secret);
        setSetupMode(true);
      } else {
        setMessage(data.error || 'Setup failed');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to start 2FA setup');
      setMessageType('error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleVerifyAndEnable = async () => {
    if (verifyCode.length !== 6) return;
    setActionLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/2fa/enable', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: verifyCode })
      });
      const data = await response.json();
      if (data.success) {
        setTwoFAEnabled(true);
        setSetupMode(false);
        setQrCode('');
        setSecret('');
        setVerifyCode('');
        setMessage('Two-factor authentication has been enabled successfully');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Verification failed');
        setMessageType('error');
        setVerifyCode('');
      }
    } catch (error) {
      setMessage('Failed to enable 2FA');
      setMessageType('error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisable = async () => {
    if (disableCode.length !== 6) return;
    setActionLoading(true);
    setMessage('');
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('/api/auth/2fa/disable', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code: disableCode })
      });
      const data = await response.json();
      if (data.success) {
        setTwoFAEnabled(false);
        setDisableMode(false);
        setDisableCode('');
        setMessage('Two-factor authentication has been disabled');
        setMessageType('success');
      } else {
        setMessage(data.error || 'Failed to disable 2FA');
        setMessageType('error');
        setDisableCode('');
      }
    } catch (error) {
      setMessage('Failed to disable 2FA');
      setMessageType('error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Security Settings</h1>
          <p className="text-sm text-gray-600">Manage your account security and authentication</p>
        </div>

        {/* Status Message */}
        {message && (
          <div className={`mb-6 p-4 rounded-lg border ${messageType === 'success' ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
            <div className="flex items-center">
              {messageType === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600 mr-2" />
              )}
              <span className={`text-sm font-medium ${messageType === 'success' ? 'text-green-800' : 'text-red-800'}`}>{message}</span>
            </div>
          </div>
        )}

        {/* 2FA Status Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${twoFAEnabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                <Shield className={`w-6 h-6 ${twoFAEnabled ? 'text-green-600' : 'text-gray-400'}`} />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">Two-factor authentication</h3>
                <p className="text-sm text-gray-500">Add an extra layer of security to your account using an authenticator app</p>
              </div>
            </div>
            <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${twoFAEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
              {twoFAEnabled ? (
                <><CheckCircle className="w-4 h-4 mr-1" /> Enabled</>
              ) : (
                <><XCircle className="w-4 h-4 mr-1" /> Disabled</>
              )}
            </div>
          </div>

          {/* Not in setup/disable mode */}
          {!setupMode && !disableMode && (
            <div>
              {twoFAEnabled ? (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Your account is protected with two-factor authentication. You will be asked for a verification code from your authenticator app each time you sign in.</p>
                  <button
                    onClick={() => setDisableMode(true)}
                    className="flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Disable 2FA
                  </button>
                </div>
              ) : (
                <div>
                  <p className="text-sm text-gray-600 mb-4">Protect your admin account by requiring a verification code from an authenticator app (such as Google Authenticator or Microsoft Authenticator) in addition to your password.</p>
                  <button
                    onClick={handleSetup}
                    disabled={actionLoading}
                    className="flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    {actionLoading ? 'Setting up...' : 'Set up 2FA'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Setup Mode */}
          {setupMode && (
            <div className="mt-4 space-y-6">
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <h4 className="font-medium text-blue-900 mb-2">Step 1: Scan QR code</h4>
                <p className="text-sm text-blue-700 mb-4">Open your authenticator app and scan this QR code to add your LMPEnergy account.</p>
                <div className="flex justify-center mb-4">
                  {qrCode && <img src={qrCode} alt="2FA QR Code" className="w-48 h-48 border-4 border-white rounded-lg shadow-md" />}
                </div>
                <p className="text-xs text-blue-600 text-center">
                  Can't scan? Enter this code manually: <code className="bg-blue-100 px-2 py-1 rounded text-xs font-mono break-all">{secret}</code>
                </p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                <h4 className="font-medium text-gray-900 mb-2">Step 2: Enter verification code</h4>
                <p className="text-sm text-gray-600 mb-3">Enter the 6-digit code from your authenticator app to confirm setup.</p>
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoFocus
                    className="w-40 text-center text-xl tracking-[0.3em] py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                  <button
                    onClick={handleVerifyAndEnable}
                    disabled={actionLoading || verifyCode.length !== 6}
                    className="flex items-center px-4 py-3 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Verifying...' : 'Verify & Enable'}
                  </button>
                </div>
              </div>

              <button
                onClick={() => { setSetupMode(false); setQrCode(''); setSecret(''); setVerifyCode(''); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel setup
              </button>
            </div>
          )}

          {/* Disable Mode */}
          {disableMode && (
            <div className="mt-4 space-y-4">
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <h4 className="font-medium text-red-900 mb-2">Confirm disable</h4>
                <p className="text-sm text-red-700 mb-3">Enter your current authenticator code to disable two-factor authentication. This will make your account less secure.</p>
                <div className="flex items-center space-x-3">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    autoFocus
                    className="w-40 text-center text-xl tracking-[0.3em] py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                    placeholder="000000"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/[^0-9]/g, ''))}
                  />
                  <button
                    onClick={handleDisable}
                    disabled={actionLoading || disableCode.length !== 6}
                    className="flex items-center px-4 py-3 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {actionLoading ? 'Disabling...' : 'Disable 2FA'}
                  </button>
                </div>
              </div>
              <button
                onClick={() => { setDisableMode(false); setDisableCode(''); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
            </div>
          )}
        </div>

        {/* Stripe Test Mode — super_admin only, self-scoped only */}
        {isSuperAdmin && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className={`p-3 rounded-lg ${stripeTestMode ? 'bg-amber-100' : 'bg-gray-100'}`}>
                  <FlaskConical className={`w-6 h-6 ${stripeTestMode ? 'text-amber-600' : 'text-gray-400'}`} />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Stripe test mode</h3>
                  <p className="text-sm text-gray-500">
                    Applies only to your own account. While on, your card setup and charging sessions
                    route through Stripe test keys — use test card 4242 4242 4242 4242, no real money moves.
                    Every other user always stays on live Stripe regardless of this setting.
                  </p>
                </div>
              </div>
              <div className={`flex items-center px-3 py-1 rounded-full text-sm font-medium ${stripeTestMode ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-600'}`}>
                {stripeStatusLoading ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : stripeTestMode ? (
                  <><FlaskConical className="w-4 h-4 mr-1" /> Test mode</>
                ) : (
                  <><CheckCircle className="w-4 h-4 mr-1" /> Live</>
                )}
              </div>
            </div>
            <button
              onClick={handleToggleStripeTestMode}
              disabled={stripeToggleLoading || stripeStatusLoading}
              className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg disabled:opacity-50 ${
                stripeTestMode
                  ? 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                  : 'text-white bg-amber-600 hover:bg-amber-700'
              }`}
            >
              <FlaskConical className="w-4 h-4 mr-2" />
              {stripeToggleLoading ? 'Updating...' : stripeTestMode ? 'Switch back to live' : 'Switch to test mode'}
            </button>
          </div>
        )}

        {/* Security Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recommended authenticator apps</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-lg">🔐</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Google Authenticator</p>
                <p className="text-xs text-gray-500">iOS & Android</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-lg">🛡️</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Microsoft Authenticator</p>
                <p className="text-xs text-gray-500">iOS & Android</p>
              </div>
            </div>
            <div className="flex items-center p-3 bg-gray-50 rounded-lg">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <span className="text-lg">🔑</span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Authy</p>
                <p className="text-xs text-gray-500">iOS, Android & Desktop</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
